import React from 'react';
import { LucideIcon, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { useCountUp } from './useCountUp';

export interface StatCardData {
  id: string;
  label: string;
  /** Numeric value — animated with count-up. */
  value: number | string;
  /** Optional prefix rendered before the count-up number (e.g. "$"). */
  prefix?: string;
  /** Optional suffix rendered after the count-up number (e.g. "%"). */
  suffix?: string;
  icon?: LucideIcon;
  /** Trend pill content: text + direction. Positive = green, negative = red. */
  trend?: { text: string; direction: 'up' | 'down' | 'flat' };
  /** First stat card in the row gets the hero treatment. */
  isHero?: boolean;
  /** Optional click handler (navigation shortcut). */
  onClick?: () => void;
}

/**
 * StatCard — the reusable dashboard stat card (Sections 3.4 & 4).
 *
 * Big number top-left (count-up animated), small gray label underneath,
 * optional trend pill top-right, optional icon chip. White card on the
 * tinted canvas: `--bg-card` + `--radius-lg` + `--shadow-md`.
 */
export function StatCard({ id: _id, label, value, prefix = '', suffix = '', icon: Icon, trend, isHero = false, onClick }: StatCardData) {
  const display = useCountUp(value);

  const content = (
    <>
      <div className={`stat-card-header flex items-start justify-between gap-3 min-w-0 ${Icon ? 'has-icon' : ''}`}>
        <div className="stat-value flex items-baseline gap-0.5 min-w-0 shrink">
          {prefix && <span className="text-[0.65em] font-semibold text-slate-500 shrink-0">{prefix}</span>}
          <span className="stat-value-num truncate">{display}</span>
          {suffix && <span className="text-[0.65em] font-semibold text-slate-500 shrink-0">{suffix}</span>}
        </div>
        {trend && (
          <span title={trend.text} className={`trend-pill min-w-0 ${trend.direction === 'up' ? 'positive' : trend.direction === 'down' ? 'negative' : 'neutral'}`}>
            {trend.direction === 'up' && <ArrowUpRight className="h-3 w-3 shrink-0" />}
            {trend.direction === 'down' && <ArrowDownRight className="h-3 w-3 shrink-0" />}
            {trend.direction === 'flat' && <Minus className="h-3 w-3 shrink-0" />}
            <span className="truncate min-w-0">{trend.text}</span>
          </span>
        )}
      </div>
      {Icon && (
        <div className="stat-icon" aria-hidden="true">
          <Icon className="h-5 w-5" strokeWidth={2.2} />
        </div>
      )}
      <div className="stat-label truncate">{label}</div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`stat-card ${isHero ? 'stat-card-hero' : ''} text-left w-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand`}
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={`stat-card ${isHero ? 'stat-card-hero' : ''}`}>
      {content}
    </div>
  );
}

/**
 * StatCardRow — responsive row of StatCards that renders 1→4 columns
 * depending on viewport (Section 4 shell).
 */
export function StatCardRow({ cards }: { cards: StatCardData[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
      {cards.map((card) => (
        <div key={card.id} className="contents">
          <StatCard {...card} />
        </div>
      ))}
    </div>
  );
}
